import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Users, 
  Search, 
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Mail,
  Phone,
  Building2,
  CheckCircle2,
  XCircle,
  Eye
} from 'lucide-react';

const AdminResponsabili = () => {
  const [loading, setLoading] = useState(true);
  const [responsabili, setResponsabili] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchResponsabili();
  }, []);

  const fetchResponsabili = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/admin/responsabili`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponsabili(response.data || []);
    } catch (error) {
      console.error('Error fetching responsabili:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort
  const filteredAndSortedData = useMemo(() => {
    let result = [...responsabili];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.name?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term) ||
        r.phone?.toLowerCase().includes(term) ||
        r.city?.toLowerCase().includes(term) ||
        r.iban?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [responsabili, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  const exportCSV = () => {
    const headers = ['Nome', 'Email', 'Telefono', 'Città', 'Codice Fiscale', 'Banca', 'Intestatario', 'IBAN', 'BIC/SWIFT'];
    const rows = filteredAndSortedData.map(r => [
      r.name || '',
      r.email || '',
      r.phone || '',
      r.city || '',
      r.fiscal_code || '',
      r.bank_name || '',
      r.account_holder || '',
      r.iban || '',
      r.bic_swift || ''
    ]);
    
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `responsabili_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-600">Caricamento responsabili...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="admin-responsabili">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gestione Responsabili</h1>
          <p className="text-slate-500">
            {filteredAndSortedData.length} responsabili {searchTerm && `(filtrati da ${responsabili.length})`}
          </p>
        </div>
        <Button variant="outline" onClick={exportCSV}>
          <Download className="h-4 w-4 mr-2" />
          Esporta CSV
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Cerca per nome, email, telefono, città o IBAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredAndSortedData.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Users className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-xl font-medium">Nessun responsabile trovato</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[calc(100vh-300px)]">
              <table className="w-full" data-testid="responsabili-table">
                <thead className="bg-slate-50 border-b sticky top-0 z-10">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Nome <SortIcon column="name" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center">
                        Email <SortIcon column="email" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50 hidden md:table-cell"
                      onClick={() => handleSort('phone')}
                    >
                      <div className="flex items-center">
                        Telefono <SortIcon column="phone" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50 hidden lg:table-cell"
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center">
                        Città <SortIcon column="city" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50 hidden xl:table-cell"
                      onClick={() => handleSort('bank_name')}
                    >
                      <div className="flex items-center">
                        Banca <SortIcon column="bank_name" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 bg-slate-50"
                      onClick={() => handleSort('iban')}
                    >
                      <div className="flex items-center">
                        IBAN <SortIcon column="iban" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 bg-slate-50">
                      Dati Completi
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 bg-slate-50">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAndSortedData.map((user, idx) => {
                    const hasCompleteBank = user.iban && user.bic_swift && user.bank_name;
                    
                    return (
                      <tr 
                        key={user.id || idx}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-800">
                            {user.name || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
                            {user.email || '-'}
                          </a>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {user.phone ? (
                            <a href={`tel:${user.phone}`} className="text-slate-600 hover:underline">
                              {user.phone}
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                          {user.city || '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden xl:table-cell">
                          {user.bank_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-slate-600">
                            {user.iban || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasCompleteBank ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Sì
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              <XCircle className="h-3 w-3 mr-1" />
                              No
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedUser(user)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <Card className="w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Dettagli Responsabile</h2>
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>✕</Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dati Personali */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Dati Personali
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Nome:</span> <strong>{selectedUser.name || '-'}</strong></p>
                    <p><span className="text-slate-500">Email:</span> <strong>{selectedUser.email || '-'}</strong></p>
                    <p><span className="text-slate-500">Telefono:</span> <strong>{selectedUser.phone || '-'}</strong></p>
                    <p><span className="text-slate-500">Codice Fiscale:</span> <strong>{selectedUser.fiscal_code || '-'}</strong></p>
                    <p><span className="text-slate-500">Indirizzo:</span> <strong>{selectedUser.address || '-'}</strong></p>
                    <p><span className="text-slate-500">CAP:</span> <strong>{selectedUser.cap || '-'}</strong></p>
                    <p><span className="text-slate-500">Città:</span> <strong>{selectedUser.city || '-'}</strong></p>
                  </div>
                </div>

                {/* Dati Bancari */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> Coordinate Bancarie
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Banca:</span> <strong>{selectedUser.bank_name || '-'}</strong></p>
                    <p><span className="text-slate-500">Intestatario:</span> <strong>{selectedUser.account_holder || '-'}</strong></p>
                    <p><span className="text-slate-500">IBAN:</span> <strong className="font-mono">{selectedUser.iban || '-'}</strong></p>
                    <p><span className="text-slate-500">BIC/SWIFT:</span> <strong className="font-mono">{selectedUser.bic_swift || '-'}</strong></p>
                  </div>
                  
                  {selectedUser.iban && selectedUser.bic_swift ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      Coordinate complete
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg text-orange-700 text-sm">
                      <XCircle className="h-4 w-4" />
                      Coordinate incomplete
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminResponsabili;
